import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Video, CheckCircle, XCircle, Loader2, AlertCircle } from "lucide-react";

export default function ZoomConnectionCard() {
  // Fetch Zoom status
  const {
    data: zoomStatus,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["zoom-status"],
    queryFn: () => apiClient.get("/api/zoom/status"),
    staleTime: 60 * 1000, // 1 minute
  });

  const isConfigured = zoomStatus?.configured;
  const isWorking = zoomStatus?.working;

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Video className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Zoom Telemedicine</CardTitle>
            <CardDescription>
              Automatic video meeting creation for telemedicine appointments
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking Zoom status...
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-red-600">
            <XCircle className="w-4 h-4" />
            Failed to check Zoom status
          </div>
        ) : isConfigured && isWorking ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Active</span>
            </div>
            <p className="text-sm text-slate-600">
              Zoom meetings will be automatically created when patients book
              telemedicine appointments (doctor-only services without a room).
            </p>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">
                <span className="font-medium">How it works:</span> When a telemedicine
                service is booked, a Zoom meeting is automatically created and both
                the patient and doctor receive email notifications with their meeting links.
              </p>
            </div>
          </div>
        ) : isConfigured && !isWorking ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Configuration Issue</span>
            </div>
            <p className="text-sm text-slate-600">
              Zoom is configured but the connection validation failed. Please check
              the server logs or contact support.
            </p>
            {zoomStatus?.error && (
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Error:</span> {zoomStatus.error}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-slate-500">
              <XCircle className="w-5 h-5" />
              <span>Not Configured</span>
            </div>
            <p className="text-sm text-slate-600">
              Zoom integration requires server-side configuration. Contact your
              system administrator to enable telemedicine video meetings.
            </p>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-500">
                Required environment variables: <code className="bg-slate-200 px-1 rounded">ZOOM_ACCOUNT_ID</code>,{" "}
                <code className="bg-slate-200 px-1 rounded">ZOOM_CLIENT_ID</code>,{" "}
                <code className="bg-slate-200 px-1 rounded">ZOOM_CLIENT_SECRET</code>
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
